import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminSplaLicenses } from '../../../hooks/useAdminSplaCatalog';
import { DECIMAL_RE } from '../../../utils/money';
import { SPLA_SUBJECT_TYPE_LABELS } from '../../utils/splaSubjects';
import type {
  SplaConsumptionCreateRequest,
  SplaConsumptionRow,
  SplaConsumptionUpdateRequest,
  SplaSubjectType,
} from '../../../types/models/Spla';
import type { ApiResponseSplaConsumption } from '../../../types/api/ApiResponseSpla';
import type { ApiResponseClientList } from '../../../types/api/ApiResponseClient';
import ClientTypeahead, { type ClientOption } from '../ProductsAndPrices/ClientTypeahead';

const SUBJECT_LABEL_MAX = 160;
const NOTE_MAX = 255;

// Strictly positive: DECIMAL_RE allows zero, the backend does not.
const isPositiveQuantity = (value: string) => DECIMAL_RE.test(value) && !/^0+(\.0+)?$/.test(value);

// Backend field-error keys rendered inline on the matching input; anything else (body, id, …)
// lands in the form-level alert.
const FIELD_ERROR_KEYS = new Set([
  'clientID',
  'spla_license_id',
  'subject_type',
  'subject_ref',
  'subject_label',
  'quantity',
  'note',
]);

interface SplaConsumptionModalProps {
  isOpen: boolean;
  /** `null` = create; a row = edit (parent keys the mount so state is always fresh). */
  row: SplaConsumptionRow | null;
  onClose: () => void;
  /** Hand the saved row back: `isCreate` tells the tab to refetch vs patch in place. */
  onSaved: (row: SplaConsumptionRow, isCreate: boolean) => void;
}

/**
 * Create or edit one per-subject consumption row.
 *
 * Identity (client, license, subject type + ref) is IMMUTABLE after create — the agent upsert key
 * never drifts, so moving a subject is delete + create. Edit therefore only offers quantity,
 * subject label, and note; the identity is shown read-only.
 *
 * Rows with `source: "computed"` are machine-maintained by a binding: an edit flips them to
 * `user`, and the NEXT RECOMPUTE RECLAIMS THEM (the edit is overwritten) — the warning alert says
 * so and points at the Bindings tab for durable changes.
 */
function SplaConsumptionModal({ isOpen, row, onClose, onSaved }: SplaConsumptionModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const isEdit = row !== null;

  // Create-mode pickers: full client list (fails gracefully during impersonation — the
  // BackupAttributionModal pattern) and the NON-archived license list (the API refuses new
  // consumption on retired licenses, so they are not even offered).
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const licenseFilters = useMemo(() => ({ agreementType: null, archived: '0' as const }), []);
  const {
    licenses,
    loading: licensesLoading,
    error: licensesError,
  } = useAdminSplaLicenses(isOpen && !isEdit, licenseFilters);

  const [clientId, setClientId] = useState<number | null>(row?.client.id ?? null);
  const [licenseId, setLicenseId] = useState<string>(row !== null ? String(row.license.id) : '');
  const [subjectType, setSubjectType] = useState<SplaSubjectType>(row?.subject.type ?? 'manual');
  const [subjectRef, setSubjectRef] = useState(row?.subject.ref ?? '');
  const [subjectLabel, setSubjectLabel] = useState(row?.subject.label ?? '');
  const [quantity, setQuantity] = useState(row?.quantity ?? '');
  const [note, setNote] = useState(row?.note ?? '');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // `apiFetch` gets a NEW identity on every OIDC silent renew — fetch the client list exactly once
  // per mount so a renew never re-runs it mid-edit.
  const hasFetchedClients = useRef(false);

  const fetchClients = useCallback(async () => {
    setClientsLoading(true);
    setClientsError(null);
    try {
      const response = await apiFetch('/clients');
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseClientList | null;
      if (body?.status === 'success' && body.data?.clients) {
        setClients(body.data.clients.map((client) => ({ id: client.id, name: client.name })));
      } else {
        setClientsError(body?.message ?? 'The client list could not be loaded.');
      }
    } catch (err) {
      setClientsError(err instanceof Error ? err.message : 'The client list could not be loaded.');
    } finally {
      setClientsLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (isEdit || hasFetchedClients.current) {
      return;
    }
    hasFetchedClients.current = true;
    void fetchClients();
  }, [isEdit, fetchClients]);

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const trimmedRef = subjectRef.trim();
  const trimmedLabel = subjectLabel.trim();
  const trimmedQuantity = quantity.trim();
  const trimmedNote = note.trim();

  // Typed subjects carry the numeric NetBox id; `manual` a free stable name.
  const isRefValid = subjectType === 'manual' ? trimmedRef.length > 0 : /^\d+$/.test(trimmedRef);
  const isLabelValid = trimmedLabel.length <= SUBJECT_LABEL_MAX;
  const isQuantityValid = isPositiveQuantity(trimmedQuantity);
  const isNoteValid = trimmedNote.length <= NOTE_MAX;

  const buildUpdateBody = (original: SplaConsumptionRow): SplaConsumptionUpdateRequest => {
    const body: SplaConsumptionUpdateRequest = {};
    if (trimmedQuantity !== original.quantity) {
      body.quantity = trimmedQuantity;
    }
    if (trimmedLabel !== (original.subject.label ?? '')) {
      body.subject_label = trimmedLabel === '' ? null : trimmedLabel;
    }
    if (trimmedNote !== (original.note ?? '')) {
      body.note = trimmedNote === '' ? null : trimmedNote;
    }
    return body;
  };

  const hasChanges = row === null || Object.keys(buildUpdateBody(row)).length > 0;
  const isFormValid = isEdit
    ? isQuantityValid && isLabelValid && isNoteValid
    : clientId !== null && licenseId !== '' && isRefValid && isQuantityValid && isLabelValid && isNoteValid;
  const canSubmit = !isSaving && isFormValid && hasChanges;

  const buildCreateBody = (): SplaConsumptionCreateRequest => {
    const body: SplaConsumptionCreateRequest = {
      clientID: clientId as number,
      spla_license_id: Number(licenseId),
      subject_type: subjectType,
      subject_ref: subjectType === 'manual' ? trimmedRef : Number(trimmedRef),
      quantity: trimmedQuantity,
    };
    if (trimmedLabel !== '') {
      body.subject_label = trimmedLabel;
    }
    if (trimmedNote !== '') {
      body.note = trimmedNote;
    }
    return body;
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const response = await apiFetch(row !== null ? `/admin/spla/consumption/${row.id}` : '/admin/spla/consumption', {
        method: row !== null ? 'PATCH' : 'POST',
        body: JSON.stringify(row !== null ? buildUpdateBody(row) : buildCreateBody()),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseSplaConsumption | null;
      if (body?.status === 'success' && body.data?.consumption) {
        const saved = body.data.consumption;
        onSaved(saved, !isEdit);
        addNotification({
          title: isEdit ? 'Consumption updated' : 'Consumption added',
          srTitle: 'Success alert',
          description: `${saved.subject.name} consumes ${saved.quantity} × ${saved.license.part_number ?? `license #${saved.license.id}`} for ${saved.client.name ?? `client #${saved.client.id}`}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onClose();
        return;
      }

      // Split the field-keyed validation map: known keys inline (the duplicate-row refusal arrives
      // under subject_ref, verbatim), the rest — incl. the cross-field pseudo-field `body` — in the
      // form-level alert.
      const errors: Record<string, string> = {};
      const generalParts: string[] = [];
      if (body?.data) {
        for (const [key, value] of Object.entries(body.data)) {
          if (typeof value !== 'string') {
            continue;
          }
          if (FIELD_ERROR_KEYS.has(key)) {
            errors[key] = value;
          } else {
            generalParts.push(value);
          }
        }
      }
      const general =
        generalParts.length > 0
          ? generalParts.join(' ')
          : Object.keys(errors).length === 0
            ? (body?.message ?? 'Could not save the consumption row. Please try again.')
            : null;
      setFieldErrors(errors);
      setFormError(general);
      addNotification({
        title: isEdit ? 'Could not update the consumption' : 'Could not add the consumption',
        srTitle: 'Error alert',
        description: general ?? Object.values(errors)[0] ?? 'Please review the highlighted fields.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
      addNotification({
        title: isEdit ? 'Could not update the consumption' : 'Could not add the consumption',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const fieldHelper = (key: string, fallback?: string) =>
    (fieldErrors[key] !== undefined || fallback !== undefined) && (
      <FormHelperText>
        <HelperText>
          <HelperTextItem variant={fieldErrors[key] !== undefined ? 'error' : 'default'}>
            {fieldErrors[key] ?? fallback}
          </HelperTextItem>
        </HelperText>
      </FormHelperText>
    );

  const licenseOptionLabel = (partNumber: string, family: string, agreement: string, offering: string) =>
    `${partNumber} — ${family} (${agreement}${agreement === 'Academic' ? ` · ${offering}` : ''})`;

  return (
    <Modal
      variant="medium"
      isOpen={isOpen}
      onClose={onClose}
      aria-label={isEdit ? 'Edit consumption row' : 'Add consumption'}
    >
      <ModalHeader
        title={isEdit ? 'Edit consumption' : 'Add consumption'}
        description={
          isEdit
            ? undefined
            : 'One row per consuming subject — a VM, a bare-metal server, or a manual name (a SQL instance, RDS users, …).'
        }
      />
      <ModalBody>
        <Form
          id="spla-consumption-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) {
              void handleSubmit();
            }
          }}
        >
          {formError !== null && (
            <Alert variant="danger" isInline title={isEdit ? 'Update failed' : 'Creation failed'}>
              {formError}
            </Alert>
          )}

          {isEdit && row.source === 'computed' && (
            <Alert variant="warning" isInline title="This row is machine-maintained by a binding">
              Editing it flips the row to <strong>user</strong>, and the next recompute reclaims it — your
              change will be overwritten. For a durable change, adjust the binding on the Bindings tab.
            </Alert>
          )}

          {isEdit ? (
            // Identity is immutable — show it read-only instead of disabled inputs pretending
            // otherwise. Moving a subject = delete + create (the agent upsert key never drifts).
            <>
              <DescriptionList isHorizontal isCompact>
                <DescriptionListGroup>
                  <DescriptionListTerm>Client</DescriptionListTerm>
                  <DescriptionListDescription>
                    {row.client.name ?? `Client #${row.client.id}`}
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>License</DescriptionListTerm>
                  <DescriptionListDescription>
                    {row.license.part_number ?? `#${row.license.id}`} — {row.license.product_family ?? ''}
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Subject</DescriptionListTerm>
                  <DescriptionListDescription>
                    {SPLA_SUBJECT_TYPE_LABELS[row.subject.type]} · {row.subject.ref}
                  </DescriptionListDescription>
                </DescriptionListGroup>
              </DescriptionList>
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    Identity is immutable — moving a subject = delete + create (the agent upsert key never
                    drifts).
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </>
          ) : (
            <>
              <FormGroup label="Client" isRequired fieldId="spla-consumption-client-input">
                {clientsError === null ? (
                  <ClientTypeahead
                    clients={clients}
                    value={clientId}
                    onChange={(id) => {
                      setClientId(id);
                      clearFieldError('clientID');
                    }}
                    isDisabled={isSaving || clientsLoading}
                    idPrefix="spla-consumption-client"
                  />
                ) : (
                  <Alert variant="warning" isInline title="The client list is unavailable">
                    {clientsError} End the impersonation session (or reopen this dialog) to pick a client.
                  </Alert>
                )}
                {fieldHelper('clientID', 'The consuming organization — the Ancalagon house client is allowed.')}
              </FormGroup>

              <FormGroup label="License" isRequired fieldId="spla-consumption-license">
                {licensesError !== null && (
                  <Alert variant="warning" isInline title="Could not load the license catalog">
                    {licensesError}
                  </Alert>
                )}
                <FormSelect
                  id="spla-consumption-license"
                  value={licenseId}
                  onChange={(_event, value) => {
                    setLicenseId(value);
                    clearFieldError('spla_license_id');
                  }}
                  aria-label="Consumed license"
                  isDisabled={isSaving || licensesLoading}
                >
                  <FormSelectOption
                    value=""
                    label={licensesLoading ? 'Loading licenses…' : 'Select a license'}
                    isDisabled
                  />
                  {licenses.map((license) => (
                    <FormSelectOption
                      key={license.id}
                      value={String(license.id)}
                      label={licenseOptionLabel(
                        license.part_number,
                        license.product_family,
                        license.agreement_type,
                        license.offering,
                      )}
                    />
                  ))}
                </FormSelect>
                {fieldHelper('spla_license_id', 'Retired licenses gain no new consumption and are not offered.')}
              </FormGroup>

              <FormGroup label="Subject type" fieldId="spla-consumption-subject-type">
                <FormSelect
                  id="spla-consumption-subject-type"
                  value={subjectType}
                  onChange={(_event, value) => {
                    setSubjectType(value as SplaSubjectType);
                    clearFieldError('subject_type');
                    clearFieldError('subject_ref');
                  }}
                  aria-label="Subject type"
                  isDisabled={isSaving}
                >
                  <FormSelectOption value="virtual_machine" label="Virtual machine" />
                  <FormSelectOption value="device" label="Server (device)" />
                  <FormSelectOption value="manual" label="Manual" />
                </FormSelect>
                {fieldHelper('subject_type')}
              </FormGroup>

              <FormGroup
                label={subjectType === 'manual' ? 'Subject name' : 'NetBox id'}
                isRequired
                fieldId="spla-consumption-subject-ref"
              >
                <TextInput
                  id="spla-consumption-subject-ref"
                  type="text"
                  inputMode={subjectType === 'manual' ? 'text' : 'numeric'}
                  value={subjectRef}
                  placeholder={subjectType === 'manual' ? 'sql-instance-erp' : '4242'}
                  onChange={(_event, value) => {
                    setSubjectRef(value);
                    clearFieldError('subject_ref');
                  }}
                  validated={
                    fieldErrors['subject_ref'] !== undefined || (trimmedRef !== '' && !isRefValid)
                      ? 'error'
                      : 'default'
                  }
                  isDisabled={isSaving}
                />
                {fieldHelper(
                  'subject_ref',
                  subjectType === 'manual'
                    ? 'A stable name for the consumer (a SQL instance, RDS users, …) — it is the upsert identity.'
                    : trimmedRef !== '' && !isRefValid
                      ? 'Enter the numeric NetBox id.'
                      : 'The numeric NetBox id of the subject.',
                )}
              </FormGroup>
            </>
          )}

          <FormGroup label="Subject label" fieldId="spla-consumption-subject-label">
            <TextInput
              id="spla-consumption-subject-label"
              type="text"
              value={subjectLabel}
              placeholder="broker.ancalagon.be"
              onChange={(_event, value) => {
                setSubjectLabel(value);
                clearFieldError('subject_label');
              }}
              validated={fieldErrors['subject_label'] !== undefined || !isLabelValid ? 'error' : 'default'}
              isDisabled={isSaving}
            />
            {fieldHelper(
              'subject_label',
              !isLabelValid
                ? `At most ${SUBJECT_LABEL_MAX} characters.`
                : 'Optional display name (e.g. the VM hostname); the ref is shown when empty.',
            )}
          </FormGroup>

          <FormGroup label="Quantity" isRequired fieldId="spla-consumption-quantity">
            <TextInput
              id="spla-consumption-quantity"
              type="text"
              inputMode="decimal"
              value={quantity}
              placeholder="8"
              onChange={(_event, value) => {
                setQuantity(value);
                clearFieldError('quantity');
              }}
              validated={
                fieldErrors['quantity'] !== undefined || (trimmedQuantity !== '' && !isQuantityValid)
                  ? 'error'
                  : 'default'
              }
              isDisabled={isSaving}
              aria-label="Units consumed per month"
            />
            {fieldHelper(
              'quantity',
              trimmedQuantity !== '' && !isQuantityValid
                ? 'Enter a strictly positive amount with up to 4 decimal places.'
                : 'Units this subject consumes per month (e.g. two-core packs).',
            )}
          </FormGroup>

          <FormGroup label="Note" fieldId="spla-consumption-note">
            <TextInput
              id="spla-consumption-note"
              type="text"
              value={note}
              placeholder="palpatine has 16 cores -> 8 dual-core packs"
              onChange={(_event, value) => {
                setNote(value);
                clearFieldError('note');
              }}
              validated={fieldErrors['note'] !== undefined || !isNoteValid ? 'error' : 'default'}
              isDisabled={isSaving}
            />
            {fieldHelper(
              'note',
              !isNoteValid
                ? `At most ${NOTE_MAX} characters.`
                : `Internal staff memo — never shown to the tenant.${isEdit ? ' Clearing it removes the note.' : ''}`,
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="spla-consumption-form"
          isDisabled={!canSubmit}
          isLoading={isSaving}
        >
          {isEdit ? 'Save' : 'Add'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default SplaConsumptionModal;
